import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { CmsSeo } from "@interfaces";
import {
    applyHeadTags,
    clearManagedHeadTags,
    mapSeoToHeadTags,
    mapJsonLdToHeadTags,
    getSiteMeta,
    absoluteUrl,
    notifyHeadApplied,
    theme,
} from "@utilities";

interface IPageHeadProps {
    seo?: CmsSeo | null;
    /** og:type override, e.g. "article" for blog posts. */
    ogType?: string;
    /**
     * JSON-LD structured-data node(s) to emit as
     * `<script type="application/ld+json">`. Null/undefined entries are skipped,
     * so conditional builders (e.g. `buildBreadcrumbJsonLd` → null) pass through.
     */
    jsonLd?: Array<Record<string, any> | null | undefined> | Record<string, any> | null;
}

/**
 * Shared page-head mechanism: maps a content record's SEO fields into the
 * document <head> (title / description / canonical / Open Graph / Twitter) and
 * optionally emits JSON-LD structured data. Site-wide defaults (brand name,
 * default OG image, Twitter handle) come from `getSiteMeta`. Renders nothing.
 */
export const PageHead = ({ seo, ogType, jsonLd }: IPageHeadProps) => {
    const location = useLocation();

    useEffect(() => {
        const site = getSiteMeta();
        const origin = site.siteUrl || (typeof window !== "undefined" ? window.location.origin : "");
        const seoTags = seo
            ? mapSeoToHeadTags(seo, {
                  origin,
                  path: location.pathname,
                  siteName: theme.title,
                  ogType,
                  defaultOgImage: absoluteUrl(site.defaultOgImage, origin),
                  twitterHandle: site.twitterHandle,
              })
            : [];
        const jsonLdTags = mapJsonLdToHeadTags(jsonLd);
        if (!seoTags.length && !jsonLdTags.length) return;
        applyHeadTags([...seoTags, ...jsonLdTags]);
        // Signal analytics that this route's title/metadata is now final, so the
        // page_view reports the correct per-page title even after an async fetch.
        notifyHeadApplied();
        return () => clearManagedHeadTags();
    }, [seo, ogType, jsonLd, location.pathname]);

    return null;
};
